import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  linkedSignal,
  output,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  HandleSideBarService,
  NavItem,
  SideBarFacadeService,
} from '@data-access/shared';
import { MatRippleModule } from '@angular/material/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatRippleModule],
  selector: 'wcg-side-nav-tabs',
  styleUrl: './nav.component.css',
  template: `
    <nav class="mat-tab-nav">
      @for (item of navItems().values(); track item.path) {
        @if (item.path) {
          <a
            href="#"
            (click)="$event.preventDefault(); onTabClick(item)"
            [class.mat-tab-active]="selectedTab() === item.path"
            class="mat-tab-link"
            role="button"
            matRipple
          >
            {{ item.label }}
            @if (selectedTab() === item.path) {
              <span class="mat-tab-indicator"></span>
            }
          </a>
        }
      }
    </nav>
  `,
})
export class SideNavTabsComponent {
  readonly navItems = input<Set<NavItem>>(new Set<NavItem>([]));
  readonly selectedTab = input<string>('');
  tabSelected = output<NavItem>();

  onTabClick(item: NavItem) {
    this.tabSelected.emit(item);
  }
}
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatSidenavModule,
    NgTemplateOutlet,
    MatCardModule,
    MatIconModule,
    NgTemplateOutlet,
    NgClass,
    SideNavTabsComponent,
    MatTooltipModule,
  ],
  selector: 'wcg-side-bar',
  styles: `
    .side-bar-container {
      display: flex;
      transition: width 0.3s ease;
      overflow: hidden !important;
      height: 30rem;

      .mat-drawer {
        transition:
          width 0.3s ease,
          opacity 0.3s ease;
        width: 100%;
        opacity: 1;
      }

      .mat-drawer.collapsed {
        width: 0;
        opacity: 0;
      }

      .mat-drawer-content {
        flex: 1;
        transition: margin 0.3s ease;
        background-color: #fff;
      }
    }

    /*
 @NOTE: Collapse, expand classes
*/
    .collapsed {
      width: 6rem;
    }
    .side-bar__title-border {
      border-bottom: 1px solid var(--color-grey-10);
    }
    .side-bar__header-icon.mat-icon {
      font-size: 1.6rem;
      width: 1.6rem;
      height: 1.6rem;
      line-height: 1;
      display: inline-flex;
      align-items: center;
      align-self: center;
    }
    .sidebar-open-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .todo-count__badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background-color: var(--color-icon-icon-critical);
      color: #fff;
      height: 22px;
      min-width: 22px;
      padding: 0 6px;
      border-radius: 11px;
      font-size: 1rem;
      font-weight: 600;
      line-height: 22px;
      margin-top: 0.25rem;
    }
  `,
  template: `
    <mat-drawer-container
      class="side-bar-container rounded-lg"
      [style.height]="height()"
      [ngClass]="{ collapsed: !drawer.opened }"
    >
      <mat-drawer
        #drawer
        class="example-sidenav"
        mode="side"
        position="end"
        [ngClass]="{ collapsed: !drawer.opened }"
        [opened]="$toggleSideBar()"
      >
        <div class="w-full flex flex-col h-full">
          <div
            class="w-full flex justify-between items-center px-4 side-bar__title-border"
          >
            @if (sidebarHeader()) {
              <div class="flex items-center gap-2 py-4">
                <h4>{{ sidebarHeader() }}</h4>
                @if (headerTooltip()) {
                  <mat-icon
                    fontSet="material-symbols-outlined"
                    class="side-bar__header-icon"
                    [matTooltip]="headerTooltip()"
                    matTooltipPosition="right"
                  >
                    info
                  </mat-icon>
                }
              </div>
            }
            @if (navItems().size > 0) {
              <wcg-side-nav-tabs
                [navItems]="navItems()"
                [selectedTab]="selectedTab()"
                (tabSelected)="onTabSelected($event)"
              />
            }
            <div class="flex gap-4">
              @defer (when exportButtonTemplate()) {
                <ng-container [ngTemplateOutlet]="exportButtonTemplate()!" />
              }
              <mat-icon
                fontSet="material-symbols-outlined"
                class="cursor-pointer"
                data-testid="close-sidebar-button"
                (click)="updateToggleSideBar(false)"
                >{{ closeIcon() }}</mat-icon
              >
            </div>
          </div>
          <div class="w-full py-6 px-4">
            <ng-template #sideBar />
          </div>
        </div>
      </mat-drawer>
      <mat-drawer-content>
        <div class="p-6">
          <button
            type="button"
            mat-flat-button
            class="sidebar-open-btn"
            (click)="updateToggleSideBar(true)"
            data-testid="open-sidebar-button"
          >
            <mat-icon fontSet="material-symbols-outlined">{{
              openIcon()
            }}</mat-icon>
            @if (todoCount() > 0) {
              <span class="todo-count__badge" data-testid="todo-count-badge">{{
                todoCount()
              }}</span>
            }
          </button>
        </div>
      </mat-drawer-content>
    </mat-drawer-container>
  `,
})
export class WcgSideBarComponent {
  protected readonly open = open;
  protected readonly parseInt = parseInt;
  private readonly _sideBarFacadeService = inject(SideBarFacadeService);
  private readonly _handleSideBarService = inject(HandleSideBarService);
  readonly sidebarHeader = input<string>('');
  readonly headerTooltip = input<string>('');
  readonly height = input<string>('68vh');
  readonly closeIcon = input<string>('chevron_right');
  readonly openIcon = input<string>('chevron_left');
  readonly navItems = input<Set<NavItem>>(new Set<NavItem>());
  readonly selectedTab = input<string>('');
  readonly todoCount = input<number>(0);

  sidebarOpened = output<boolean>();
  tabSelected = output<NavItem>();

  readonly $toggleSideBar = linkedSignal({
    computation: (toggleSideBar) => {
      const _toggleSideBar = toggleSideBar;
      this.sidebarOpened.emit(toggleSideBar);
      return _toggleSideBar;
    },
    source: this._sideBarFacadeService.$toggleSideBar,
  });

  readonly sideBarContainerRef = viewChild('sideBar', {
    read: ViewContainerRef,
  });

  readonly exportButtonTemplate = input<TemplateRef<unknown>>();

  updateToggleSideBar(toggleSideBar: boolean) {
    this._handleSideBarService.setToggleSideBarOpen(toggleSideBar);
  }

  onTabSelected(item: NavItem) {
    this.tabSelected.emit(item);
  }
}
